// src/utils/zip.ts
/**
 * Minimal ZIP archive writer (STORE method, no compression).
 *
 * Exists so we can emit real .xlsx files — which are ZIP containers of XML
 * parts — without pulling in a spreadsheet dependency. Deliberately supports
 * only what OOXML needs: small ASCII-named entries, no compression, no
 * encryption, no Zip64.
 */

const encoder = new TextEncoder();

const CRC_TABLE: Uint32Array = (() => {
	const table = new Uint32Array(256);
	for (let n = 0; n < 256; n++) {
		let c = n;
		for (let k = 0; k < 8; k++) {
			c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
		}
		table[n] = c >>> 0;
	}
	return table;
})();

export function crc32(bytes: Uint8Array): number {
	let c = 0xffffffff;
	for (let i = 0; i < bytes.length; i++) {
		c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
	}
	return (c ^ 0xffffffff) >>> 0;
}

export type ZipEntry = {
	/** Path inside the archive, e.g. "xl/worksheets/sheet1.xml". */
	name: string;
	content: string;
};

/** MS-DOS packed date/time, as required by the ZIP local file header. */
function dosDateTime(d: Date): { time: number; date: number } {
	const time =
		((d.getHours() & 0x1f) << 11) |
		((d.getMinutes() & 0x3f) << 5) |
		((d.getSeconds() >> 1) & 0x1f);
	const date =
		(((d.getFullYear() - 1980) & 0x7f) << 9) |
		(((d.getMonth() + 1) & 0x0f) << 5) |
		(d.getDate() & 0x1f);
	return { time, date };
}

export function createZip(entries: readonly ZipEntry[], now = new Date()): Uint8Array {
	const { time, date } = dosDateTime(now);

	const files = entries.map((entry) => {
		const nameBytes = encoder.encode(entry.name);
		const dataBytes = encoder.encode(entry.content);
		return { nameBytes, dataBytes, crc: crc32(dataBytes) };
	});

	const localSize = files.reduce(
		(sum, f) => sum + 30 + f.nameBytes.length + f.dataBytes.length,
		0,
	);
	const centralSize = files.reduce((sum, f) => sum + 46 + f.nameBytes.length, 0);

	const out = new Uint8Array(localSize + centralSize + 22);
	const view = new DataView(out.buffer);
	let offset = 0;

	const u16 = (v: number) => {
		view.setUint16(offset, v, true);
		offset += 2;
	};
	const u32 = (v: number) => {
		view.setUint32(offset, v, true);
		offset += 4;
	};
	const bytes = (b: Uint8Array) => {
		out.set(b, offset);
		offset += b.length;
	};

	// Local file headers + data.
	const localOffsets: number[] = [];
	for (const f of files) {
		localOffsets.push(offset);
		u32(0x04034b50); // local file header signature
		u16(20); // version needed to extract (2.0)
		u16(0x0800); // general purpose flags: UTF-8 filenames
		u16(0); // compression method: stored
		u16(time);
		u16(date);
		u32(f.crc);
		u32(f.dataBytes.length); // compressed size
		u32(f.dataBytes.length); // uncompressed size
		u16(f.nameBytes.length);
		u16(0); // extra field length
		bytes(f.nameBytes);
		bytes(f.dataBytes);
	}

	// Central directory.
	const centralStart = offset;
	files.forEach((f, i) => {
		u32(0x02014b50); // central file header signature
		u16(20); // version made by
		u16(20); // version needed to extract
		u16(0x0800); // general purpose flags
		u16(0); // compression method: stored
		u16(time);
		u16(date);
		u32(f.crc);
		u32(f.dataBytes.length);
		u32(f.dataBytes.length);
		u16(f.nameBytes.length);
		u16(0); // extra field length
		u16(0); // file comment length
		u16(0); // disk number start
		u16(0); // internal file attributes
		u32(0); // external file attributes
		u32(localOffsets[i]);
		bytes(f.nameBytes);
	});

	const centralEnd = offset;

	// End of central directory record.
	u32(0x06054b50);
	u16(0); // disk number
	u16(0); // disk with central directory
	u16(files.length);
	u16(files.length);
	u32(centralEnd - centralStart);
	u32(centralStart);
	u16(0); // comment length

	return out;
}
